import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { matchPath, useLocation } from 'react-router-dom';
import { ChangeLocale } from 'shared-components';
import { isRBCRightPay } from 'shared-components/src/utils/whitelabel';
// Import Global Components
import ExternalLink from '../../../../components/ExternalLink';
import Link from '../../../../components/Link';
// Import Local Components
import Logout from '../../components/Logout';
// Import Component Styles
import { HeaderNav, LinksGroup, LinksContainer, NavMenu } from './styles';
// Import Links
import LINKS from '../../links';
// Import Translations
import { navEn } from '../../../../i18n/nav';
// Import Contexts
import { ProgramConfigContext } from '../../../../contexts/ProgramConfigContext';
// Import Utils
import { hasTransfersEnabled } from '../../../../utils/transfers';

const HeaderNavWrapper = ({
  setLocale,
  locale,
  localization,
  open,
  setOpen,
  animate,
  page,
}) => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const configs = useContext(ProgramConfigContext);

  const transfersEnabled = hasTransfersEnabled(configs);

  const links = useMemo(() => {
    // RBC RightPay has no About My Card knowledge base
    const allLinks = isRBCRightPay() ? LINKS.filter(l => l.for !== 'about') : [...LINKS];
    // Tax documents sit directly after Transfers in the menu
    const transfersIdx = allLinks.findIndex(l => l.for === 'transfers');
    if (configs.t4a_enabled) {
      allLinks.splice(transfersIdx + 1, 0, { to: '/tax-documents', for: 'tax-documents' });
    }
    // Hide the Transfers link when the program has no transfer types enabled
    if (!transfersEnabled) {
      allLinks.splice(transfersIdx, 1);
    }
    return allLinks;
  }, [configs.t4a_enabled, transfersEnabled]);

  // Dashboard ("/") only matches itself; other sections also match their nested routes
  const isCurrent = to => Boolean(matchPath({ path: to, end: to === '/' }, pathname));

  const renderLinks = links =>
    links.map((link, idx) => (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions,react/no-array-index-key
      <li key={idx} onClick={() => setOpen(!open)}>
        {link.externalLink ? (
          <ExternalLink
            page={page}
            color="headerText"
            link={link.externalLink}
            linkText={navEn[`nav-${link.for}-link`]}
            linkFor={link.for}
          />
        ) : (
          <Link
            page={page}
            type="headerText"
            to={link.to}
            aria-current={isCurrent(link.to) ? 'page' : undefined}
            link={navEn[`nav-${link.for}-link`]}
            linkFor={link.for}
          />
        )}
      </li>
    ));

  return (
    <HeaderNav open={open} $animate={animate}>
      <NavMenu>
        <LinksContainer>
          <LinksGroup>{renderLinks(links)}</LinksGroup>
          <li>
            <Logout />
          </li>
        </LinksContainer>
        {localization && (
          <ChangeLocale
            setLocale={setLocale}
            locale={locale}
            messages={intl.messages}
          />
        )}
      </NavMenu>
    </HeaderNav>
  );
};

HeaderNavWrapper.propTypes = {
  setLocale: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  localization: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  // Enables the slide/stagger transitions (only once the menu button has been used)
  animate: PropTypes.bool,
  page: PropTypes.string.isRequired,
};

HeaderNavWrapper.defaultProps = {
  animate: false,
};

export default HeaderNavWrapper;
